import React from 'react';
import { Box, Button, Card, CardContent, Container, Grid2, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthorBlock } from '../../../components/AuthorBlock';
import { ExpandableParagraph } from '../../../components/ExpandableParagraph';
import ToolShed from '../../../components/ToolShed';
import { PlanDetails, PlanAuthor, Tool, QuickOrderCost } from '../types';
import { InstructionVideoItem, PlanImageItem } from '../../../types';
import { CutsBlock } from './PlanViewInfoTabCutsBlock';
import useAuth from '../../../components/Auth';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { PlanPrimaryInfoSection } from './PlanPrimaryInfoSection';
import { SimpleVideoList } from '../../../components/SimpleVideoList';
import { SimpleImageList } from '../../../components/SimpleImageList';

interface PlanViewInfoTabNativeContentProps {
    plan: PlanDetails;
    planAuthor: PlanAuthor;
	planImages: PlanImageItem[];
    instructionsVideos: InstructionVideoItem[];
    companyTools: Tool[];
    quickOrderCosts: QuickOrderCost[];
    handleViewCutsClick: () => void;
    handleAddTool: (toolId: number) => void;
    handleRemoveTool: (toolId: number) => void;
	onImageClick: (imageIndex: number) => void;
}

export const PlanViewInfoTabNativeContent = ({plan, planAuthor, planImages, instructionsVideos, companyTools, quickOrderCosts, handleViewCutsClick, handleAddTool, handleRemoveTool, onImageClick}: PlanViewInfoTabNativeContentProps) => {
	const {userProfile} = useAuth();
	const companyToolsSet = new Set(companyTools.map((tool) => tool.id));
	const theme = useTheme();
	const {t} = useTranslation();
	const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
	return <>
		<Container>
			<PlanPrimaryInfoSection planId={plan.id} quickOrderCosts={quickOrderCosts} timeCost={plan.timeCost} ratingsSummary={plan.ratingsSummary} isBuyNowEnabled={Boolean(quickOrderCosts.length > 0)} tags={plan.tags} />
		</Container>
		<Container disableGutters={!isSmUp}>
			<Card elevation={0}>
				<CardContent>
					<Grid2 container direction={isSmUp ? 'row' : 'column'} spacing={2}>
						<Grid2 size={{xs: 12, md: 6}}>
							<ExpandableParagraph text={plan.description} />
						</Grid2>
						<Grid2 size={{xs: 12, md: 6}}>
							<SimpleImageList 
								images={planImages} 
								onItemClick={(image, index) => onImageClick(index)}
							/>
						</Grid2>
					</Grid2>
				</CardContent>
			</Card>
		</Container>
		<Container>
			{Boolean(instructionsVideos.length) && <Typography variant='h4' sx={{mt: 4, mb: 2}}>{t('planView.watchInstructions')}</Typography>}
			{!instructionsVideos.length && plan.instructions && <Typography variant='h4' sx={{mt: 4, mb: 2}}>{t('planView.steps')}</Typography>}
			<Grid2 container>
				<Grid2 size={{xs: 12, md: 8}}>
					<SimpleVideoList
						videos={instructionsVideos
							.filter((video) => video.source === 'manual' && video.videoUrl)
							.map((video) => ({
								videoUrl: video.videoUrl || '',
								id: video.id,
								source: 'manual',
							}))}
					/>
				</Grid2>
				{plan.instructions && <Grid2>
					<ExpandableParagraph text={plan.instructions} />
				</Grid2>}
				{plan.cutsUrl && <Grid2 size={12}>
					<CutsBlock cutsUrl={plan.cutsUrl} onClick={handleViewCutsClick} />
				</Grid2>}
				{Boolean(plan.tools && plan.tools.length > 0) && <Grid2 size={{xs: 12, md: 8}} mt={5} offset={{xs: 0, md: 2}}>
					<ToolShed 
						title={t('planViewMaterialsCard.toolsUsed')}
						tools={plan.tools.map((tool) => tool.tool ? tool.tool : ({
							key: tool.name,
						}))} 
						companyToolsSet={userProfile ? companyToolsSet : undefined}
						onAddTool={userProfile ? handleAddTool : undefined}
						onRemoveTool={userProfile ? handleRemoveTool : undefined}
						sx={{mt: 4}}
					/>
				</Grid2>}
			</Grid2>
			<Box display='flex' justifyContent='center' sx={{mt: 5}}>
				<Button component={Link} variant='contained' to={`/plans/${plan.id}/supplies`} size='large' color='primary' endIcon={<ArrowForwardIcon />}>
					{t('planView.toSuppliesTab')}
				</Button>
			</Box>
			<Box mt={12}>
				<AuthorBlock authorInfo={planAuthor} />
			</Box>
		</Container>
	</>;
};